import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Spinner,
  Table,
} from "reactstrap";
import {
  getStoreProjectSummary,
  updateStoreProject,
  refreshStoreProjectQuotePrices,
} from "../service";
import {
  applyStoreProjectCoupon,
  searchStoreCoupons,
} from "../couponsService";
import { mergeSummaryCreditExtensions } from "../creditNotes/mergeSummaryExtensions";
import { getDealerDiscountSummary } from "../creditNotes/dealerDiscountSummary";
import DealerOrderCard from "./DealerOrderCard";
import OrderSummaryPriceMetaRows, {
  formatDeliveryChargeAmount,
  headerItemsTotalFromPriceMeta,
  payableTotalFromPriceMeta,
} from "./OrderSummaryPriceMetaRows";
import { isZerobuildDomain } from "../../utils";
import { toErrorText } from "../pages/storeSalesDashboardHelpers";
import { AppContext } from "../../context";
import { isBuyerProfileCompleteForPlaceOrder } from "../pages/storeDocumentHelpers";
import BuyerProfilePlaceOrderNotice from "./BuyerProfilePlaceOrderNotice";
export const STORE_ORDER_GST_RATE = 0.18;

const STORE_SUPPORT_PHONE = "9150469996";
export const storeAmountInclGst = (exTax) => {
  const n = exTax == null || exTax === "" ? null : Number(exTax);

  return n != null && Number.isFinite(n)
    ? n * (1 + STORE_ORDER_GST_RATE)
    : null;
};

export const storeGstOn = (exTax) => {
  const n = exTax == null || exTax === "" ? null : Number(exTax);

  return n != null && Number.isFinite(n) ? n * STORE_ORDER_GST_RATE : null;
};

const isHttpUrl = (value) => {
  if (typeof value !== "string") return false;

  return /^https?:\/\//i.test(value.trim());
};

const renderLocationView = (value) => {
  if (!isHttpUrl(value)) return <span className="fw-semibold">{value}</span>;

  return (
    <a
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      className="fw-semibold text-decoration-none"
    >
      Open map <i className="fa-solid fa-arrow-up-right-from-square ms-1" />
    </a>
  );
};

const validateDeliveryContact = (value) => {
  if (value === "") return null;
  const digits = value.replace(/\D/g, "");

  if (digits.length !== 10) return "Enter a valid 10-digit phone number.";

  return null;
};

const parseDeliveryContactsFromStored = (raw) => {
  const text = raw == null ? "" : String(raw).trim();
  if (!text) return [""];

  const parts = text
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  return parts.length > 0 ? parts : [""];
};

const serializeDeliveryContacts = (contacts) =>
  contacts
    .map((value) => String(value).trim())
    .filter((value) => value.length > 0)
    .join(", ");

const validateDeliveryContacts = (contacts) => {
  const digitsList = [];

  for (const value of contacts) {
    const trimmed = String(value).trim();
    if (!trimmed) continue;

    const fieldError = validateDeliveryContact(trimmed);
    if (fieldError) return fieldError;

    const digits = trimmed.replace(/\D/g, "");
    if (digitsList.includes(digits)) {
      return "Each contact number must be unique.";
    }
    digitsList.push(digits);
  }

  return null;
};

const canEditStoreDeliveryAddressFields = (summaryPayload) =>
  summaryPayload?.delivery_address_fields_editable === true;

const canEditStoreDeliveryContact = (summaryPayload) =>
  summaryPayload?.delivery_contact_editable === true;

export default function StoreProjectPriceSummary({
  isOpen,
  projectId,
  canManageProjectItems = false,
  isBusy = false,
  hidePricing = false,
  isSampleQuote = false,
  dealerOrderRow = null,
  onDealerReviewOrder,
  acceptingDealerOrder = false,
  onClose,
  onPlaceOrder,
  onBeginPlaceOrder,
  externalPlaceOrderConfirmMode = null,
  onExternalPlaceOrderConfirmHandled,
  onHeaderPayableFromSummary,
  onQuotePricesRefreshed,
  onIncludesUpdated,
  items = [],
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [showPlaceOrderConfirm, setShowPlaceOrderConfirm] = useState(false);
  const [viewMode, setViewMode] = useState("summary");
  const [selectedShipmentIndex, setSelectedShipmentIndex] = useState(0);
  const [deliveryEditing, setDeliveryEditing] = useState(false);
  const [deliveryDraft, setDeliveryDraft] = useState({
    pincode: "",
    address: "",
    contacts: [""],
    location: "",
  });
  const [deliverySaveBusy, setDeliverySaveBusy] = useState(false);
  const [deliverySaveError, setDeliverySaveError] = useState(null);
  const [refreshQuoteBusy, setRefreshQuoteBusy] = useState(false);
  const [includeHardwares, setIncludeHardwares] = useState(true);
  const [includeAccessories, setIncludeAccessories] = useState(true);
  const [includeHardwaresBusy, setIncludeHardwaresBusy] = useState(false);
  const [includeAccessoriesBusy, setIncludeAccessoriesBusy] = useState(false);
  const [showHardwaresModal, setShowHardwaresModal] = useState(false);
  const [showAccessoriesModal, setShowAccessoriesModal] = useState(false);
  const [showPriceUpdatedNotice, setShowPriceUpdatedNotice] = useState(false);
  const priceUpdatedFlashTimeoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponCodeDraft, setCouponCodeDraft] = useState("");
  const [couponSearchResults, setCouponSearchResults] = useState([]);
  const [couponSearchBusy, setCouponSearchBusy] = useState(false);
  const [couponApplyBusy, setCouponApplyBusy] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const { currentUser, openProfileModal } = useContext(AppContext) || {};
  const buyerProfileComplete = isBuyerProfileCompleteForPlaceOrder(
    currentUser?.buyer
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const showPriceUpdatedFlash = useCallback(() => {
    setShowPriceUpdatedNotice(true);
    if (priceUpdatedFlashTimeoutRef.current) {
      clearTimeout(priceUpdatedFlashTimeoutRef.current);
    }
    priceUpdatedFlashTimeoutRef.current = setTimeout(() => {
      setShowPriceUpdatedNotice(false);
      priceUpdatedFlashTimeoutRef.current = null;
    }, 2000);
  }, []);

  useEffect(
    () => () => {
      if (priceUpdatedFlashTimeoutRef.current) {
        clearTimeout(priceUpdatedFlashTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!isOpen) {
      setShowPriceUpdatedNotice(false);
      if (priceUpdatedFlashTimeoutRef.current) {
        clearTimeout(priceUpdatedFlashTimeoutRef.current);
        priceUpdatedFlashTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  const useZbColumnOffcanvas = isZerobuildDomain() && !isMobile;

  const toNumberOrNull = (value) => {
    if (value == null || value === "") return null;

    const n = Number(value);

    return Number.isFinite(n) ? n : null;
  };

  const formatCurrency = (value) => {
    const n = toNumberOrNull(value);

    if (n == null) return "—";

    return `₹${Math.round(n).toLocaleString("en-IN")}`;
  };

  const formatDeliveryCharge = (value) =>
    formatDeliveryChargeAmount(value, formatCurrency);

  const reloadSummary = useCallback(async () => {
    if (!projectId) return null;
    const body = await getStoreProjectSummary(projectId);
    setSummaryData(body);
    return body;
  }, [projectId]);

  useEffect(() => {
    if (!isOpen || !projectId) return;

    let cancelled = false;

    const loadSummary = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const body = await getStoreProjectSummary(projectId);

        if (cancelled) return;

        setSummaryData(body);
      } catch (e) {
        if (cancelled) return;

        setError(
          e?.response?.data?.error ||
            e?.message ||
            "Unable to load project summary."
        );
        setSummaryData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId]);

  useEffect(() => {
    if (!isOpen) {
      setViewMode("summary");
      setSelectedShipmentIndex(0);
      setDeliveryEditing(false);
      setDeliverySaveError(null);
      return;
    }
    setViewMode("summary");
    setSelectedShipmentIndex(0);
  }, [isOpen, projectId]);

  const rawSummaryPayload = summaryData?.data;

  useEffect(() => {
    if (rawSummaryPayload == null) return;
    if (includeHardwaresBusy || includeAccessoriesBusy) return;

    setIncludeHardwares(rawSummaryPayload.hardwares_included !== false);
    setIncludeAccessories(rawSummaryPayload.accessories_included !== false);
  }, [
    rawSummaryPayload,
    rawSummaryPayload?.hardwares_included,
    rawSummaryPayload?.accessories_included,
    includeHardwaresBusy,
    includeAccessoriesBusy,
  ]);

  const handleConfirmPlaceOrder = async (mode) => {
    if (isBusy) return;

    const didPlace = await onPlaceOrder?.(mode);

    if (didPlace === true) {
      setShowPlaceOrderConfirm(false);
      onClose?.();
    }
  };

  const requestPlaceOrderConfirm = useCallback(
    (mode) => {
      if (typeof onBeginPlaceOrder === "function") {
        onBeginPlaceOrder(mode);
        return;
      }
      setShowPlaceOrderConfirm(mode);
    },
    [onBeginPlaceOrder]
  );

  useEffect(() => {
    if (externalPlaceOrderConfirmMode == null) return;
    setShowPlaceOrderConfirm(externalPlaceOrderConfirmMode);
    onExternalPlaceOrderConfirmHandled?.();
  }, [
    externalPlaceOrderConfirmMode,
    onExternalPlaceOrderConfirmHandled,
  ]);

  const summaryPayload = useMemo(
    () => mergeSummaryCreditExtensions(rawSummaryPayload),
    [rawSummaryPayload]
  );

  const placementMode = String(summaryPayload?.mode || "")
    .trim()
    .toLowerCase();

  const isDealerPlacementMode = placementMode === "dealer";
  const isDirectPlacementMode = placementMode === "direct";
  const priceMeta = summaryPayload?.price_meta;
  const appliedCoupons = Array.isArray(summaryPayload?.coupons)
    ? summaryPayload.coupons
    : [];
  const isDraftProject = summaryPayload?.status === "draft";
  const canEditCoupons = canManageProjectItems && isDraftProject;

  const handleSearchCoupons = useCallback(async () => {
    if (!projectId || !isDraftProject || couponCodeDraft.trim() === "") {
      setCouponSearchResults([]);
      return;
    }
    setCouponSearchBusy(true);
    setCouponError(null);
    try {
      const response = await searchStoreCoupons(couponCodeDraft.trim(), projectId);
      setCouponSearchResults(Array.isArray(response.coupons) ? response.coupons : []);
    } catch (e) {
      setCouponError(toErrorText(e, "Unable to search coupons."));
      setCouponSearchResults([]);
    } finally {
      setCouponSearchBusy(false);
    }
  }, [couponCodeDraft, isDraftProject, projectId]);

  const handleApplyCoupon = useCallback(
    async (code) => {
      if (!projectId || !isDraftProject) return;
      setCouponApplyBusy(true);
      setCouponError(null);
      try {
        await applyStoreProjectCoupon(projectId, { code });
        await reloadSummary();
        setShowCouponModal(false);
        setCouponCodeDraft("");
        setCouponSearchResults([]);
      } catch (e) {
        setCouponError(toErrorText(e, "Unable to apply coupon."));
      } finally {
        setCouponApplyBusy(false);
      }
    },
    [isDraftProject, projectId, reloadSummary]
  );

  useEffect(() => {
    if (!isDraftProject) {
      setShowCouponModal(false);
    }
  }, [isDraftProject]);

  const useDirectPriceMeta =
    isDirectPlacementMode && priceMeta != null && typeof priceMeta === "object";

  const dealerDiscounts = summaryPayload?.dealer_discounts;
  const itemsPriceRaw = summaryPayload?.items_price;

  const { itemsAfterDiscount } = useMemo(
    () => getDealerDiscountSummary(itemsPriceRaw, dealerDiscounts),
    [itemsPriceRaw, dealerDiscounts]
  );

  const itemsPrice = itemsAfterDiscount;
  const shipments = summaryPayload?.shipments || [];
  const showAgainstOrderCombinedNotice =
    summaryPayload?.has_mixed_standard_and_against_order_products === true;
  const canEditIncludeFlags =
    Boolean(projectId) &&
    canManageProjectItems &&
    summaryPayload?.status === "draft";
  const includeFlagsUpdating = includeHardwaresBusy || includeAccessoriesBusy;

  const deliveryTotalExTax = shipments.reduce((sum, shipment) => {
    const n = toNumberOrNull(shipment?.charge);
    return sum + (n ?? 0);
  }, 0);

  const itemsExTax = toNumberOrNull(itemsPrice);
  const itemsGst = storeGstOn(itemsExTax);
  const itemsWithGst = storeAmountInclGst(itemsExTax);
  const deliveryInclGst = storeAmountInclGst(deliveryTotalExTax);
  const deliveryReferenceTotalInclGst = storeAmountInclGst(
    summaryPayload?.delivery_reference_total_inr
  );
  const deliveryReferencePerShipmentInclGst = storeAmountInclGst(
    summaryPayload?.delivery_reference_charge_per_shipment
  );
  const hardwareRows = useMemo(() => {
    return Array.isArray(summaryPayload?.hardware_items)
      ? summaryPayload.hardware_items
      : [];
  }, [summaryPayload?.hardware_items]);

  const hasHardwareRows = hardwareRows.length > 0;
  const accessoryRows = useMemo(() => {
    return Array.isArray(summaryPayload?.accessory_items)
      ? summaryPayload.accessory_items
      : [];
  }, [summaryPayload?.accessory_items]);

  const hasAccessoryRows = accessoryRows.length > 0;
  const payableTotalFromMeta = payableTotalFromPriceMeta(priceMeta);

  const payableTotal =
    useDirectPriceMeta && payableTotalFromMeta != null
      ? payableTotalFromMeta
      : itemsWithGst != null && deliveryInclGst != null
      ? itemsWithGst + deliveryInclGst
      : null;

  const deliveryRoadKm = toNumberOrNull(
    summaryPayload?.delivery_road_distance_km
  );

  const deliveryChargesBracketLabel = (() => {
    const parts = [];
    if (deliveryRoadKm != null) {
      parts.push(`~${Math.round(deliveryRoadKm)} km`);
    }

    if (parts.length === 0) return null;
    return `(${parts.join(", ")})`;
  })();

  const deliveryTotalsDifferEnough = (reference, charged) =>
    reference != null &&
    charged != null &&
    Math.round(reference - charged) !== 0;

  const renderStrikeThenAmount = ({ referenceTotal, chargedTotal }) =>
    deliveryTotalsDifferEnough(referenceTotal, chargedTotal) ? (
      <>
        <span className="text-decoration-line-through text-muted me-2">
          {formatCurrency(referenceTotal)}
        </span>
        <span className="fw-semibold">
          {formatDeliveryCharge(chargedTotal)}
        </span>
      </>
    ) : (
      <span className="fw-semibold">{formatDeliveryCharge(chargedTotal)}</span>
    );

  const storedDeliveryPincode = summaryPayload?.delivery_pincode;
  const inferredDeliveryPincode = summaryPayload?.inferred_delivery_pincode;
  const deliveryPincodeInferred = summaryPayload?.delivery_pincode_inferred;
  const storedDeliveryAddress = summaryPayload?.delivery_address ?? null;
  const storedDeliveryContact = summaryPayload?.delivery_contact ?? null;
  const storedDeliveryLocation = summaryPayload?.delivery_location ?? null;
  const dealer = summaryPayload?.dealer ?? null;
  const dealerName = dealer?.name;
  const dealerPhone = dealer?.phone;
  const dealerAssigned = summaryPayload?.has_buyer_dealer === true;

  const hasStoredDeliveryAddress =
    String(storedDeliveryAddress || "").trim().length > 0;
  const canPlaceOrder = hasStoredDeliveryAddress && buyerProfileComplete;
  const canPlaceWithDealer = canPlaceOrder && dealerAssigned;

  const displayedPincode =
    storedDeliveryPincode || inferredDeliveryPincode || null;
  const canEditDeliveryAddressFields =
    canEditStoreDeliveryAddressFields(summaryPayload);
  const canEditDeliveryContact = canEditStoreDeliveryContact(summaryPayload);
  const canEditAnyDeliveryFields =
    canEditDeliveryAddressFields || canEditDeliveryContact;
  const canUpdateDeliveryDetails =
    summaryPayload?.can_update_delivery_details === true;
  const canEditDeliveryDetails =
    canEditAnyDeliveryFields &&
    (canManageProjectItems || canUpdateDeliveryDetails);

  const quotePricesStale = summaryPayload?.quote_prices_stale === true;
  const showStaleQuotePrices =
    quotePricesStale && canManageProjectItems === true;
  const liveItemsPriceWt = toNumberOrNull(
    summaryPayload?.live_items_price_without_tax
  );

  useEffect(() => {
    if (
      !isOpen ||
      isLoading ||
      !summaryPayload ||
      !onHeaderPayableFromSummary ||
      hidePricing
    ) {
      return;
    }

    const dealerMeta = summaryPayload?.dealer_price_meta;
    if (
      isDealerPlacementMode &&
      dealerMeta != null &&
      typeof dealerMeta === "object"
    ) {
      const headerFromDealerMeta = headerItemsTotalFromPriceMeta(dealerMeta);
      if (headerFromDealerMeta != null) {
        onHeaderPayableFromSummary(headerFromDealerMeta);
        return;
      }
    }

    const headerFromMeta = headerItemsTotalFromPriceMeta(priceMeta);
    if (useDirectPriceMeta && headerFromMeta != null) {
      onHeaderPayableFromSummary(headerFromMeta);
      return;
    }
    if (itemsWithGst != null && itemsWithGst > 0) {
      onHeaderPayableFromSummary(itemsWithGst);
    }
  }, [
    isOpen,
    isLoading,
    summaryPayload,
    itemsWithGst,
    onHeaderPayableFromSummary,
    hidePricing,
    useDirectPriceMeta,
    isDealerPlacementMode,
    priceMeta,
  ]);

  const startDeliveryEdit = () => {
    setDeliverySaveError(null);
    setDeliveryDraft({
      pincode: String(storedDeliveryPincode || displayedPincode || "")
        .replace(/\D/g, "")
        .slice(0, 6),
      address: storedDeliveryAddress || "",
      contacts: parseDeliveryContactsFromStored(storedDeliveryContact),
      location: storedDeliveryLocation || "",
    });
    setDeliveryEditing(true);
  };

  const cancelDeliveryEdit = () => {
    setDeliverySaveError(null);
    setDeliveryEditing(false);
  };

  const saveDeliveryDetails = async () => {
    if (!projectId || deliverySaveBusy) return;

    const pincode = deliveryDraft.pincode.replace(/\D/g, "");
    const address = deliveryDraft.address.trim();
    const contacts = deliveryDraft.contacts;
    const location = deliveryDraft.location.trim();

    if (canEditDeliveryAddressFields && pincode.length !== 6) {
      setDeliverySaveError("Enter a valid 6-digit pincode.");
      return;
    }
    if (canEditDeliveryContact) {
      const contactError = validateDeliveryContacts(contacts);
      if (contactError) {
        setDeliverySaveError(contactError);
        return;
      }
    }

    setDeliverySaveBusy(true);
    setDeliverySaveError(null);
    try {
      const deliveryUpdatePayload = {};
      if (canEditDeliveryAddressFields) {
        deliveryUpdatePayload.delivery_pincode = pincode;
        deliveryUpdatePayload.delivery_address = address;
        deliveryUpdatePayload.delivery_location = location;
      }
      if (canEditDeliveryContact) {
        deliveryUpdatePayload.delivery_contact =
          serializeDeliveryContacts(contacts);
      }
      await updateStoreProject(projectId, deliveryUpdatePayload);
      const refreshedSummary = await getStoreProjectSummary(projectId);
      setSummaryData(refreshedSummary);
      setDeliveryEditing(false);
      setError(null);
    } catch (e) {
      const apiMsg =
        e?.response?.data?.error?.message ??
        (typeof e?.error === "object" && e.error?.message) ??
        null;
      setDeliverySaveError(
        apiMsg || e?.error || e?.message || "Unable to save delivery details."
      );
    } finally {
      setDeliverySaveBusy(false);
    }
  };

  const handleToggleIncludeHardwares = async (checked) => {
    if (!projectId || !canEditIncludeFlags || isBusy || includeHardwaresBusy) {
      return;
    }

    const previous = includeHardwares;
    setIncludeHardwares(checked);
    setIncludeHardwaresBusy(true);
    setError(null);

    try {
      await updateStoreProject(projectId, { hardwares_included: checked });
      const refreshedSummary = await getStoreProjectSummary(projectId);
      setSummaryData(refreshedSummary);
      onIncludesUpdated?.();
      showPriceUpdatedFlash();
    } catch (e) {
      setIncludeHardwares(previous);
      setError(
        e?.response?.data?.error?.message ??
          e?.response?.data?.error ??
          e?.message ??
          "Unable to update hardwares."
      );
    } finally {
      setIncludeHardwaresBusy(false);
    }
  };

  const handleToggleIncludeAccessories = async (checked) => {
    if (
      !projectId ||
      !canEditIncludeFlags ||
      isBusy ||
      includeAccessoriesBusy
    ) {
      return;
    }

    const previous = includeAccessories;
    setIncludeAccessories(checked);
    setIncludeAccessoriesBusy(true);
    setError(null);

    try {
      await updateStoreProject(projectId, { accessories_included: checked });
      const refreshedSummary = await getStoreProjectSummary(projectId);
      setSummaryData(refreshedSummary);
      onIncludesUpdated?.();
      showPriceUpdatedFlash();
    } catch (e) {
      setIncludeAccessories(previous);
      setError(
        e?.response?.data?.error?.message ??
          e?.response?.data?.error ??
          e?.message ??
          "Unable to update accessories."
      );
    } finally {
      setIncludeAccessoriesBusy(false);
    }
  };

  const handleRefreshQuotePricesInSummary = async () => {
    if (!projectId || refreshQuoteBusy) return;
    setRefreshQuoteBusy(true);
    setError(null);
    try {
      await refreshStoreProjectQuotePrices(projectId);
      const refreshedSummary = await getStoreProjectSummary(projectId);
      setSummaryData(refreshedSummary);
      onQuotePricesRefreshed?.();
    } catch (e) {
      setError(
        e?.response?.data?.error?.message ??
          e?.response?.data?.error ??
          e?.message ??
          "Unable to refresh quote prices."
      );
    } finally {
      setRefreshQuoteBusy(false);
    }
  };

  function formatDate(date) {
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }

  const renderCombinedAddressView = () => {
    const addressLine = String(storedDeliveryAddress || "").trim();
    const contactLines = parseDeliveryContactsFromStored(storedDeliveryContact).filter(
      (line) => String(line).trim().length > 0
    );
    const locationLine = String(storedDeliveryLocation || "").trim();
    const hasAny =
      addressLine || contactLines.length > 0 || locationLine;

    if (!hasAny) {
      return <span className="text-muted">—</span>;
    }

    return (
      <div className="d-flex flex-column gap-1 small">
        {addressLine ? <span className="text-break">{addressLine}</span> : null}
        {contactLines.length > 0 ? (
          <div>
            <span className="text-muted">
              {contactLines.length === 1 ? "Contact: " : "Contacts: "}
            </span>
            {contactLines.map((line, index) => (
              <span key={`${line}-${index}`}>
                {index > 0 ? ", " : null}
                {line}
              </span>
            ))}
          </div>
        ) : null}
        {locationLine ? (
          <span>
            <span className="text-muted">Map: </span>
            {renderLocationView(storedDeliveryLocation)}
          </span>
        ) : null}
      </div>
    );
  };

  const selectedShipment =
    shipments[selectedShipmentIndex] &&
    typeof shipments[selectedShipmentIndex] === "object"
      ? shipments[selectedShipmentIndex]
      : null;
  const shipmentItems = Array.isArray(selectedShipment?.items)
    ? selectedShipment.items
    : [];
  const isShipmentMode = viewMode === "shipments";

  const buildShipmentItemsTree = (items) => {
    const mkNode = (key, name) => ({
      key,
      name: name || "Folder",
      items: [],
      children: [],
      _childrenMap: new Map(),
    });

    const root = mkNode("root", "Root");
    const list = Array.isArray(items) ? items : [];

    list.forEach((item) => {
      const parents = Array.isArray(item?.parents) ? item.parents : [];
      let cursor = root;

      parents.forEach((parent, idx) => {
        const parentName =
          typeof parent?.name === "string" && parent.name.trim()
            ? parent.name.trim()
            : "Folder";
        const parentKeyRaw = parent?.id ?? `name:${parentName}`;
        const parentKey = `${String(parentKeyRaw)}:${idx}`;

        let child = cursor._childrenMap.get(parentKey);
        if (!child) {
          child = mkNode(parentKey, parentName);
          cursor._childrenMap.set(parentKey, child);
          cursor.children.push(child);
        }
        cursor = child;
      });

      cursor.items.push(item);
    });

    return root;
  };

  const renderShipmentItem = (item, key) => (
    <div key={key} className="border rounded p-2">
      <div className="fw-semibold">{item?.name || "—"}</div>
      <div className="text-muted small">
        {item?.height ?? "—"} x {item?.width ?? "—"} x {item?.depth ?? "—"} -{" "}
        {item?.range ?? "—"}
      </div>
    </div>
  );

  const renderShipmentTree = (node, depth = 0) => {
    const indentLevel = Math.max(depth - 1, 0);
    const indentPx = indentLevel * 12;
    const folderTitle =
      node.key === "root" ? null : (
        <div
          className="fw-semibold mb-1"
          style={{ marginLeft: `${indentPx}px` }}
        >
          {node.name || "Folder"}
        </div>
      );

    const itemRows = node.items.map((item, idx) =>
      renderShipmentItem(
        item,
        `${node.key}-item-${idx}-${item?.id ?? item?.name ?? "unknown"}`
      )
    );

    const childRows = node.children.map((child) => (
      <div key={child.key} className="mt-2">
        {renderShipmentTree(child, depth + 1)}
      </div>
    ));

    return (
      <div>
        {folderTitle}
        <div
          className="d-flex flex-column gap-2"
          style={{ marginLeft: node.key === "root" ? 0 : `${indentPx}px` }}
        >
          {itemRows}
          {childRows}
        </div>
      </div>
    );
  };

  return (
    <>
      <Offcanvas
        isOpen={isOpen}
        toggle={() => {
          if (isShipmentMode) {
            setViewMode("summary");
            setSelectedShipmentIndex(0);
            return;
          }
          onClose?.();
        }}
        direction="bottom"
        className={
          useZbColumnOffcanvas
            ? "store-price-summary-offcanvas--zb-column"
            : undefined
        }
        style={{ height: "100vh" }}
      >
        <OffcanvasHeader
          toggle={() => {
            if (isShipmentMode) {
              setViewMode("summary");
              setSelectedShipmentIndex(0);
              return;
            }
            onClose?.();
          }}
          className="border-bottom py-2 px-3"
        >
          <span className="fw-bold">
            {isShipmentMode ? "Shipments" : "Order Summary"}
          </span>
        </OffcanvasHeader>
        <OffcanvasBody className="px-3 py-2 d-flex flex-column">
          <div className="flex-grow-1 overflow-auto">
            {isLoading ? (
              <div className="text-muted small">Loading summary...</div>
            ) : error ? (
              <div className="alert alert-danger mb-0" role="alert">
                {error}
              </div>
            ) : isShipmentMode ? (
              <div className="small">
                <div className="d-flex gap-2 border-bottom pb-2 mb-2">
                  {shipments.map((shipment, idx) => (
                    <button
                      key={`shipment-tab-${idx}`}
                      type="button"
                      className={`btn btn-sm ${
                        idx === selectedShipmentIndex
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setSelectedShipmentIndex(idx)}
                    >
                      {shipment?.name}
                    </button>
                  ))}
                </div>

                <div className="mb-2">
                  <span className="text-muted me-1">Delivery Date:</span>
                  <span>{formatDate(selectedShipment?.delivery_date)}</span>
                </div>

                {shipmentItems.length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {renderShipmentTree(buildShipmentItemsTree(shipmentItems))}
                  </div>
                ) : (
                  <div className="text-muted">No shipment items.</div>
                )}
              </div>
            ) : (
              <div className="small">
                <div className="card border mb-3">
                  <div className="card-body p-2">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2 border-bottom pb-2 text-uppercase">
                      <span className="fw-semibold">Delivery Address</span>
                      {!hidePricing &&
                      canEditDeliveryDetails &&
                      !deliveryEditing ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-muted p-0 lh-1 text-decoration-none flex-shrink-0"
                          onClick={() => startDeliveryEdit()}
                          aria-label="Edit delivery details"
                          title="Edit delivery details"
                        >
                          <i className="fa-solid fa-pencil" />
                        </button>
                      ) : null}
                    </div>

                    {deliveryEditing ? (
                      <div className="d-flex flex-column gap-3">
                        {canEditDeliveryAddressFields ? (
                          <>
                            <div>
                              <label
                                htmlFor="delivery-edit-pincode"
                                className="form-label small text-muted mb-1"
                              >
                                Pincode
                              </label>
                              <input
                                id="delivery-edit-pincode"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="postal-code"
                                maxLength={6}
                                className="form-control form-control-sm"
                                style={{ maxWidth: "6.5rem" }}
                                placeholder="______"
                                value={deliveryDraft.pincode}
                                disabled={deliverySaveBusy}
                                onChange={(e) =>
                                  setDeliveryDraft((prev) => ({
                                    ...prev,
                                    pincode: e.target.value
                                      .replace(/\D/g, "")
                                      .slice(0, 6),
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="delivery-edit-address"
                                className="form-label small text-muted mb-1"
                              >
                                Delivery Address
                              </label>
                              <textarea
                                id="delivery-edit-address"
                                className="form-control form-control-sm"
                                rows={3}
                                placeholder="House / building, street, area, city, state"
                                value={deliveryDraft.address}
                                disabled={deliverySaveBusy}
                                onChange={(e) =>
                                  setDeliveryDraft((prev) => ({
                                    ...prev,
                                    address: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="delivery-edit-location"
                                className="form-label small text-muted mb-1"
                              >
                                Map location
                              </label>
                              <input
                                id="delivery-edit-location"
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Google Maps link or landmark"
                                value={deliveryDraft.location}
                                disabled={deliverySaveBusy}
                                onChange={(e) =>
                                  setDeliveryDraft((prev) => ({
                                    ...prev,
                                    location: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </>
                        ) : (
                          <div className="text-muted small">
                            Delivery address is locked after dispatch.
                          </div>
                        )}
                        {canEditDeliveryContact ? (
                          <div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="form-label small text-muted mb-0">
                                {deliveryDraft.contacts.length === 1
                                  ? "Contact"
                                  : "Contacts"}
                              </span>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-decoration-none"
                                disabled={deliverySaveBusy}
                                onClick={() =>
                                  setDeliveryDraft((prev) => ({
                                    ...prev,
                                    contacts: [...prev.contacts, ""],
                                  }))
                                }
                                aria-label="Add another contact number"
                                title="Add another contact number"
                              >
                                <i className="fa-solid fa-plus" />
                              </button>
                            </div>
                            <div className="d-flex flex-column gap-2">
                              {deliveryDraft.contacts.map(
                                (contactValue, index) => (
                                  <div
                                    key={`delivery-contact-${index}`}
                                    className="d-flex align-items-center gap-2"
                                  >
                                    <input
                                      id={
                                        index === 0
                                          ? "delivery-edit-contact"
                                          : undefined
                                      }
                                      type="tel"
                                      inputMode="tel"
                                      autoComplete="tel-national"
                                      maxLength={10}
                                      className="form-control form-control-sm"
                                      placeholder="10-digit phone number"
                                      value={contactValue}
                                      disabled={deliverySaveBusy}
                                      onChange={(e) =>
                                        setDeliveryDraft((prev) => ({
                                          ...prev,
                                          contacts: prev.contacts.map(
                                            (entry, entryIndex) =>
                                              entryIndex === index
                                                ? e.target.value
                                                : entry
                                          ),
                                        }))
                                      }
                                    />
                                    {deliveryDraft.contacts.length > 1 ? (
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm text-muted p-0 flex-shrink-0"
                                        disabled={deliverySaveBusy}
                                        onClick={() =>
                                          setDeliveryDraft((prev) => {
                                            const nextContacts =
                                              prev.contacts.filter(
                                                (_, entryIndex) =>
                                                  entryIndex !== index
                                              );

                                            return {
                                              ...prev,
                                              contacts:
                                                nextContacts.length > 0
                                                  ? nextContacts
                                                  : [""],
                                            };
                                          })
                                        }
                                        aria-label="Remove contact number"
                                        title="Remove contact number"
                                      >
                                        <i className="fa-solid fa-xmark" />
                                      </button>
                                    ) : null}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        ) : null}
                        {deliverySaveError ? (
                          <div className="text-danger small">
                            {deliverySaveError}
                          </div>
                        ) : null}
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={deliverySaveBusy}
                            onClick={() => saveDeliveryDetails()}
                          >
                            {deliverySaveBusy ? "Saving…" : "Save"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={deliverySaveBusy}
                            onClick={() => cancelDeliveryEdit()}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        <div>
                          <span className="text-muted d-block small mb-1">
                            Pincode
                          </span>
                          <span className="fw-semibold">
                            {displayedPincode || "—"}
                          </span>
                          {deliveryPincodeInferred &&
                          !storedDeliveryPincode &&
                          displayedPincode ? (
                            <span className="text-muted ms-1 small">
                              (suggested)
                            </span>
                          ) : null}
                        </div>
                        <div>
                          <span className="text-muted d-block small mb-1">
                            Address
                          </span>
                          {renderCombinedAddressView()}
                        </div>
                      </div>
                    )}
                    {!deliveryEditing && !canPlaceOrder ? (
                      <div className="text-danger fw-bold small border-top pt-2 mt-2">
                        Add a delivery address to place this order.
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="card border mb-3">
                  <div
                    className={`card-body p-2${
                      shipments.length > 0 ? " pb-0" : ""
                    }`}
                  >
                    <div className="fw-semibold mb-2 border-bottom pb-2 text-uppercase">
                      Delivery Date
                    </div>
                    {shipments.length > 0 ? (
                      shipments.map((shipment, idx) => (
                        <div
                          key={`shipment-detail-${idx}`}
                          className={`d-flex align-items-center gap-3 py-1 ps-1 flex-wrap${
                            shipments.length > 1
                              ? " justify-content-between"
                              : ""
                          }`}
                        >
                          {shipments.length > 1 ? (
                            <span className="text-muted">{shipment?.name}</span>
                          ) : null}
                          <span
                            className={
                              shipments.length > 1
                                ? undefined
                                : "fw-semibold  fs-6 text-success"
                            }
                          >
                            {formatDate(shipment?.delivery_date) || "—"}
                          </span>
                          {shipments.length > 1 ? (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-decoration-none"
                              onClick={() => {
                                setSelectedShipmentIndex(idx);
                                setViewMode("shipments");
                              }}
                            >
                              View Shipment
                            </button>
                          ) : null}
                        </div>
                      ))
                    ) : (
                      <div className="text-muted ps-3 py-1">
                        No delivery date available.
                      </div>
                    )}
                    {shipments.length > 0 ? (
                      <>
                        {showAgainstOrderCombinedNotice ? (
                          <div
                            className="alert alert-warning py-2 px-2 mt-2 mb-0 small"
                            role="note"
                          >
                            One or more products are against order or involve
                            minifix and the delivery date is adjusted
                            accordingly.
                          </div>
                        ) : null}
                        <div
                          className="alert alert-warning py-2 px-2 mt-2 mb-2 small"
                          role="note"
                        >
                          Delivery dates are subject to payment confirmation.
                          The final delivery date will be communicated via
                          WhatsApp after order acceptance.
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>

                {!hidePricing && !isSampleQuote ? (
                  <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <div className="fw-semibold text-uppercase">opt-ins</div>
                      {showPriceUpdatedNotice ? (
                        <div
                          className="px-2 py-1 rounded bg-success bg-opacity-10 border border-success small flex-shrink-0"
                          role="status"
                          aria-live="polite"
                        >
                          <span className="text-success fw-semibold">
                            Price updated
                          </span>
                        </div>
                      ) : null}
                    </div>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <input
                        id="include-hardwares-checkbox"
                        type="checkbox"
                        className="form-check-input mt-0"
                        checked={includeHardwares}
                        disabled={
                          !canEditIncludeFlags || isBusy || includeHardwaresBusy
                        }
                        onChange={(e) =>
                          handleToggleIncludeHardwares(e.target.checked)
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="include-hardwares-checkbox"
                      >
                        Include Hardwares
                      </label>
                      <a
                        href="#"
                        className="text-decoration-none small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowHardwaresModal(true);
                        }}
                      >
                        (view list)
                      </a>
                    </div>
                    {hasAccessoryRows ? (
                      <div className="d-flex align-items-center gap-2">
                        <input
                          id="include-accessories-checkbox"
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={includeAccessories}
                          disabled={
                            !canEditIncludeFlags ||
                            isBusy ||
                            includeAccessoriesBusy
                          }
                          onChange={(e) =>
                            handleToggleIncludeAccessories(e.target.checked)
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="include-accessories-checkbox"
                        >
                          Include Accessories
                        </label>
                        <a
                          href="#"
                          className="text-decoration-none small"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAccessoriesModal(true);
                          }}
                        >
                          (view list)
                        </a>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {!hidePricing &&
                (appliedCoupons.length > 0 || canEditCoupons) ? (
                  <div className="card border mb-3">
                    <div className="card-body p-2">
                      <div className="fw-semibold mb-2 border-bottom pb-2 text-uppercase">
                        Discounts
                      </div>
                      {appliedCoupons.length > 0 ? (
                        <div>
                          {appliedCoupons.map((coupon, index) => (
                            <div
                              key={coupon.id}
                              className={`d-flex align-items-center justify-content-between gap-3${
                                index > 0 ? " border-top pt-2 mt-2" : ""
                              }`}
                            >
                              <div className="d-flex align-items-center gap-2 min-w-0">
                                <div
                                  className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-2 bg-warning bg-opacity-75 text-white p-2"
                                  aria-hidden="true"
                                >
                                  <i className="fa-solid fa-tag" />
                                </div>
                                <div className="min-w-0">
                                  <div className="fw-semibold text-truncate">
                                    {coupon.code}
                                    {coupon.auto_applied ? (
                                      <span className="text-muted fw-normal ms-1 small">
                                        (auto)
                                      </span>
                                    ) : null}
                                  </div>
                                  {coupon.name ? (
                                    <div className="text-muted small text-truncate">
                                      {coupon.name}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                              <div className="d-flex flex-column align-items-end text-end flex-shrink-0">
                                <span className="fs-6 fw-bold text-success lh-sm">
                                  {formatCurrency(coupon.discount_amount)}
                                </span>
                                <span className="d-inline-flex align-items-center gap-1 mt-1 px-2 py-0 rounded-pill bg-success bg-opacity-10 text-success small fw-medium">
                                  <i
                                    className="fa-solid fa-circle-check"
                                    aria-hidden="true"
                                  />
                                  Saved
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {canEditCoupons ? (
                        <div
                          className={
                            appliedCoupons.length > 0
                              ? "border-top pt-2 mt-1"
                              : undefined
                          }
                        >
                          {appliedCoupons.length > 0 ? (
                            <button
                              type="button"
                              className="btn btn-link p-0 small text-decoration-none"
                              onClick={() => {
                                setCouponError(null);
                                setCouponCodeDraft("");
                                setCouponSearchResults([]);
                                setShowCouponModal(true);
                              }}
                            >
                              Add coupon
                            </button>
                          ) : (
                            <div className="d-flex align-items-center justify-content-between gap-2 py-1">
                              <div className="d-flex align-items-center gap-2 min-w-0">
                                <div
                                  className="d-flex align-items-center justify-content-center flex-shrink-0 rounded bg-warning text-white p-2"
                                  aria-hidden="true"
                                >
                                  <i className="fa-solid fa-tag" />
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 small text-muted text-decoration-none text-start"
                                  onClick={() => {
                                    setCouponError(null);
                                    setCouponCodeDraft("");
                                    setCouponSearchResults([]);
                                    setShowCouponModal(true);
                                  }}
                                >
                                  Add coupon
                                </button>
                              </div>
                              <Button
                                color="warning"
                                outline
                                size="sm"
                                className="flex-shrink-0"
                                onClick={() => {
                                  setCouponError(null);
                                  setCouponCodeDraft("");
                                  setCouponSearchResults([]);
                                  setShowCouponModal(true);
                                }}
                              >
                                Apply
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <hr className="my-2" />

                {!hidePricing ? (
                  <div className="fs-6">
                    <div className="fw-semibold mb-2 text-uppercase">
                      Summary
                    </div>
                    {showStaleQuotePrices ? (
                      <div
                        className="alert alert-warning py-2 px-2 mb-2 small d-flex flex-wrap align-items-center justify-content-between gap-2"
                        role="status"
                      >
                        <span>One or more product prices have changed.</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary text-nowrap"
                          onClick={() => handleRefreshQuotePricesInSummary()}
                          disabled={refreshQuoteBusy || isBusy}
                        >
                          {refreshQuoteBusy ? "Updating…" : "Update prices"}
                        </button>
                      </div>
                    ) : null}

                    {isSampleQuote && (
                      <>
                        <div className="d-flex justify-content-between py-1">
                          <span className="text-muted">Price</span>
                          <span className="text-decoration-line-through text-muted">
                            {items[0]?.price}
                          </span>
                        </div>
                      </>
                    )}

                    {useDirectPriceMeta ? (
                      <OrderSummaryPriceMetaRows
                        meta={priceMeta}
                        deliveryChargesBracketLabel={
                          deliveryChargesBracketLabel
                        }
                      />
                    ) : (
                      <>
                        {showStaleQuotePrices && liveItemsPriceWt != null ? (
                          <div className="d-flex justify-content-between py-1 text-muted">
                            <span>Items at current catalog (excl. tax)</span>
                            <span>{formatCurrency(liveItemsPriceWt)}</span>
                          </div>
                        ) : null}

                        <div className="d-flex justify-content-between py-1">
                          <span className="text-muted">Items (excl. tax)</span>
                          <span className="fw-semibold d-inline-flex align-items-center">
                            {includeFlagsUpdating ? (
                              <Spinner size="sm" aria-label="Updating price" />
                            ) : (
                              formatCurrency(itemsExTax)
                            )}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between py-1">
                          <span className="text-muted">GST on items</span>
                          <span>{formatCurrency(itemsGst)}</span>
                        </div>

                        <div className="d-flex justify-content-between py-1 border-bottom pb-2 mb-1">
                          <span className="fw-semibold">
                            Items total (incl. GST)
                          </span>
                          <span className="fw-semibold">
                            {formatCurrency(itemsWithGst)}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between py-1">
                          <span className="text-muted">
                            Delivery charges (incl. GST)
                            {deliveryChargesBracketLabel ? (
                              <span className="text-muted">
                                {" "}
                                {deliveryChargesBracketLabel}
                              </span>
                            ) : null}
                          </span>
                          <span>
                            {renderStrikeThenAmount({
                              referenceTotal: deliveryReferenceTotalInclGst,
                              chargedTotal: deliveryInclGst,
                            })}
                          </span>
                        </div>
                        <div className="text-muted small pb-1">
                          (Unloading not included)
                        </div>

                        {shipments.length > 1 &&
                          shipments.map((shipment, idx) => {
                            const chargedExTax = toNumberOrNull(
                              shipment?.charge
                            );
                            const chargedInclGst =
                              storeAmountInclGst(chargedExTax);
                            const shipmentLabel =
                              (shipment?.name &&
                                String(shipment.name).trim()) ||
                              `Shipment ${idx + 1}`;

                            return (
                              <div
                                key={`shipment-charge-${idx}`}
                                className="d-flex justify-content-between py-1 ps-3"
                              >
                                <span className="text-muted">
                                  {shipmentLabel}
                                </span>
                                <span>
                                  {deliveryTotalsDifferEnough(
                                    deliveryReferencePerShipmentInclGst,
                                    chargedInclGst
                                  ) ? (
                                    <>
                                      <span className="text-decoration-line-through text-muted me-2">
                                        {formatDeliveryCharge(
                                          deliveryReferencePerShipmentInclGst
                                        )}
                                      </span>
                                      <span>
                                        {formatDeliveryCharge(chargedInclGst)}
                                      </span>
                                    </>
                                  ) : (
                                    formatDeliveryCharge(chargedInclGst)
                                  )}
                                </span>
                              </div>
                            );
                          })}

                        <div className="d-flex justify-content-between py-1 border-top mt-1 pt-2">
                          <span className="fw-semibold">Payable total</span>
                          <span className="fw-bold">
                            {formatCurrency(payableTotal)}
                          </span>
                        </div>
                      </>
                    )}

                    {dealerAssigned ? (
                      <div className="border-top mt-2 pt-2">
                        <div className="fw-semibold text-uppercase">Dealer</div>
                        <div className="d-flex py-1 ps-3">
                          <span className="text-muted me-1">Name:</span>
                          <span>{dealerName || "—"}</span>
                        </div>
                        <div className="d-flex py-1 ps-3">
                          <span className="text-muted me-1">Phone:</span>
                          <span>{dealerPhone || "—"}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {hidePricing && dealerOrderRow ? (
                  <DealerOrderCard
                    row={dealerOrderRow}
                    embeddedInOrderSummary
                    acceptingProjectId={
                      acceptingDealerOrder ? dealerOrderRow?.id : null
                    }
                    onReviewOrder={onDealerReviewOrder}
                  />
                ) : null}
              </div>
            )}
          </div>
          {isShipmentMode ? null : hidePricing ? null : summaryPayload?.status !==
            "draft" ? null : !canManageProjectItems ? null : summaryPayload?.frozen ===
            true ? null : (
            <div className="border-top pt-3">
              <p className="small text-muted mb-3 border-bottom p-2 card">
                No payment will be processed when you place the order. Our sales
                team will contact you to collect the payment.
              </p>
              {!buyerProfileComplete ? (
                <BuyerProfilePlaceOrderNotice onOpenProfile={openProfileModal} />
              ) : null}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <Button className="bg-white border-primary">
                  <a
                    href={`tel:${STORE_SUPPORT_PHONE}`}
                    className="text-primary text-decoration-none"
                  >
                    Call us
                  </a>
                </Button>
                <div className="d-flex gap-2 align-items-center">
                  {!canPlaceOrder && (
                    <span className="text-muted small">
                      Delivery address required
                    </span>
                  )}
                  <Button
                    color={isDealerPlacementMode ? "success" : "primary"}
                    onClick={() =>
                      requestPlaceOrderConfirm(
                        isDealerPlacementMode ? "dealer" : "direct"
                      )
                    }
                    disabled={
                      isBusy ||
                      refreshQuoteBusy ||
                      showStaleQuotePrices ||
                      !canPlaceOrder ||
                      (isDealerPlacementMode && !dealerAssigned)
                    }
                    title={
                      !buyerProfileComplete
                        ? "Complete your profile to place this order."
                        : !canPlaceOrder
                        ? "Add a delivery address to place this order."
                        : isDealerPlacementMode && !dealerAssigned
                          ? "Want a dealer? Talk to sales."
                          : undefined
                    }
                  >
                    {isBusy
                      ? "Updating..."
                      : isDealerPlacementMode
                        ? "Place Order with dealer"
                        : "Place Order"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal
        isOpen={showHardwaresModal}
        toggle={() => setShowHardwaresModal(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setShowHardwaresModal(false)}>
          Hardwares
        </ModalHeader>
        <ModalBody>
          {!hasHardwareRows ? (
            <div className="text-muted small mb-0">No hardwares available</div>
          ) : (
            <div className="table-responsive">
              <Table
                bordered
                hover
                responsive
                size="sm"
                className="mb-0 align-middle"
              >
                <thead
                  style={{
                    backgroundColor: "#6c757d",
                    color: "#fff",
                  }}
                >
                  <tr>
                    <th>Item</th>
                    <th>Contents</th>
                  </tr>
                </thead>
                <tbody>
                  {hardwareRows.map((item, idx) => (
                    <tr key={`${item?.name || "hardware"}-${idx}`}>
                      <td>{String(item?.name || "—")}</td>
                      <td>{`${item?.qty ?? 0} ${item?.unit || "nos."}`}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={showAccessoriesModal}
        toggle={() => setShowAccessoriesModal(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setShowAccessoriesModal(false)}>
          Accessories
        </ModalHeader>
        <ModalBody>
          {!hasAccessoryRows ? (
            <div className="text-muted small mb-0">
              No accessories available
            </div>
          ) : (
            <div className="table-responsive">
              <Table
                bordered
                hover
                responsive
                size="sm"
                className="mb-0 align-middle"
              >
                <thead
                  style={{
                    backgroundColor: "#6c757d",
                    color: "#fff",
                  }}
                >
                  <tr>
                    <th>Item</th>
                    <th>Contents</th>
                  </tr>
                </thead>
                <tbody>
                  {accessoryRows.map((item, idx) => (
                    <tr key={`${item?.name || "accessory"}-${idx}`}>
                      <td>{String(item?.name || "—")}</td>
                      <td>{`${item?.qty ?? 0} ${item?.unit || "nos."}`}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={Boolean(showPlaceOrderConfirm)}
        toggle={() => !isBusy && setShowPlaceOrderConfirm(false)}
        centered
      >
        <ModalHeader toggle={() => !isBusy && setShowPlaceOrderConfirm(false)}>
          Confirm Place Order
        </ModalHeader>
        <ModalBody>Are you sure you want to place the order?</ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowPlaceOrderConfirm(false)}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            color="success"
            onClick={() => handleConfirmPlaceOrder(showPlaceOrderConfirm)}
            disabled={
              isBusy ||
              refreshQuoteBusy ||
              showStaleQuotePrices ||
              (showPlaceOrderConfirm === "dealer"
                ? !canPlaceWithDealer
                : !canPlaceOrder)
            }
          >
            {isBusy
              ? "Updating..."
              : showPlaceOrderConfirm === "dealer"
                ? "Place with dealer"
                : "Place direct"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showCouponModal}
        toggle={() => !couponApplyBusy && setShowCouponModal(false)}
      >
        <ModalHeader
          toggle={() => !couponApplyBusy && setShowCouponModal(false)}
        >
          Add coupon
        </ModalHeader>
        <ModalBody>
          <div className="d-flex gap-2 mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Enter coupon code"
              value={couponCodeDraft}
              onChange={(e) => setCouponCodeDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearchCoupons();
                }
              }}
            />
            <Button
              color="primary"
              outline
              onClick={handleSearchCoupons}
              disabled={couponSearchBusy || couponCodeDraft.trim() === ""}
            >
              {couponSearchBusy ? "Searching…" : "Search"}
            </Button>
          </div>
          {couponError ? (
            <div className="alert alert-danger py-2 small mb-2">
              {couponError}
            </div>
          ) : null}
          {couponSearchResults.length === 0 &&
          couponCodeDraft.trim() !== "" &&
          !couponSearchBusy ? (
            <p className="text-muted small mb-0">No matching coupons.</p>
          ) : null}
          {couponSearchResults.map((coupon) => (
            <div
              key={coupon.id}
              className="d-flex justify-content-between align-items-start border rounded p-2 mb-2"
            >
              <div>
                <div className="fw-semibold">{coupon.code}</div>
                <div className="small text-muted">{coupon.name}</div>
                {coupon.eligible === false && coupon.ineligible_reason ? (
                  <div className="small text-danger">
                    {coupon.ineligible_reason}
                  </div>
                ) : null}
              </div>
              <Button
                color="primary"
                size="sm"
                disabled={coupon.eligible === false || couponApplyBusy}
                onClick={() => handleApplyCoupon(coupon.code)}
              >
                Apply
              </Button>
            </div>
          ))}
        </ModalBody>
      </Modal>
    </>
  );
}
